import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export default function EventDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [event, setEvent] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadingBookings, setLoadingBookings] = useState(false);

  useEffect(() => {
    async function fetchEvent() {
      setLoading(true);
      try {
        const data = await api.get(`/events/${slug}`);
        setEvent(data.data.event || data.data);
      } catch (err) {
        setError(err.status === 404 ? 'Événement non trouvé' : err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchEvent();
  }, [slug]);

  // Fetch bookings if user is the event owner
  useEffect(() => {
    if (!event || !isAuthenticated || user?.id !== event.promoterId) return;

    async function fetchBookings() {
      setLoadingBookings(true);
      try {
        const data = await api.get(`/bookings/event/${event.id}`);
        setBookings(data.data.bookings || []);
      } catch (err) {
        // Silently fail for bookings · still show event
        console.error('Failed to fetch bookings:', err);
      } finally {
        setLoadingBookings(false);
      }
    }
    fetchBookings();
  }, [event, isAuthenticated, user?.id]);

  function formatDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Poster helpers · derived parts of the event date (guarded against missing/invalid)
  function parseDate(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  function posterDay(dateStr) {
    const d = parseDate(dateStr);
    return d ? d.toLocaleDateString('fr-FR', { day: 'numeric' }) : '';
  }
  function posterMonth(dateStr) {
    const d = parseDate(dateStr);
    return d ? d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '').toUpperCase() : '';
  }
  function posterYear(dateStr) {
    const d = parseDate(dateStr);
    return d ? d.toLocaleDateString('fr-FR', { year: 'numeric' }) : '';
  }
  function posterWeekday(dateStr) {
    const d = parseDate(dateStr);
    return d ? d.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', '').toUpperCase() : '';
  }
  function posterTime(dateStr) {
    const d = parseDate(dateStr);
    return d ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 animate-pulse">
        <div className="h-8 dark:bg-dark-elevated bg-light-elevated w-64 mb-4" />
        <div className="h-4 dark:bg-dark-elevated bg-light-elevated w-48 mb-8" />
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-6 dark:bg-dark-elevated bg-light-elevated w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <p className="th-text-secondary text-lg mb-4">{error}</p>
        <Link to="/" className="th-link-mono">
          ← Retour à l'accueil
        </Link>
      </div>
    );
  }

  if (!event) return null;

  const isOwner = isAuthenticated && user?.id === event.promoterId;
  const isDraft = event.status === 'draft';
  const acceptedBookings = bookings.filter((b) => b.status === 'accepted');

  const statusBadgeClass =
    event.status === 'draft'
      ? 'th-border th-text-muted'
      : event.status === 'published'
        ? 'border-accent text-accent bg-accent/10'
        : event.status === 'cancelled'
          ? 'border-status-rejected text-status-rejected bg-status-rejected/10'
          : 'th-border th-text-secondary';
  const statusLabel =
    event.status === 'draft'
      ? 'Brouillon'
      : event.status === 'published'
        ? 'Publié'
        : event.status === 'cancelled'
          ? 'Annulé'
          : 'Terminé';

  const cityCountry =
    event.location?.city && event.location?.country
      ? `${event.location.city}, ${event.location.country}`
      : event.location?.city || event.location?.country || '';

  // "Telegram" venue line, e.g. WAREHOUSE · PANTIN, FRANCE
  const telegramParts = [event.venueName, cityCountry].filter(Boolean);
  const telegramLine = telegramParts.join(' · ').toUpperCase();

  const startTime = posterTime(event.eventDate);
  const endTime = posterTime(event.endDate);

  function publishEvent() {
    api
      .patch(`/events/${event.id}/status`, { status: 'published' })
      .then(() => {
        setEvent((prev) => ({ ...prev, status: 'published' }));
      })
      .catch((err) => alert(err.message));
  }

  return (
    <div className="max-w-7xl mx-auto animate-fadeIn">
      {/* Draft banner (owner + draft) */}
      {isDraft && isOwner && (
        <div className="border-b border-status-pending bg-status-pending/10 px-4 sm:px-6 lg:px-8 py-4">
          <p className="font-mono text-[11px] text-status-pending uppercase tracking-[0.2em] font-medium">
            Événement privé · Brouillon (visible uniquement pour vous)
          </p>
        </div>
      )}

      {/* ───────── Poster header ───────── */}
      <header className="border-b th-border grid lg:grid-cols-[340px_1fr]">
        {/* Date block */}
        <div className="lg:border-r th-border flex flex-col items-center justify-center text-center px-6 py-12">
          <time dateTime={parseDate(event.eventDate)?.toISOString() || undefined}>
            <span className="block font-serif th-text text-[clamp(6rem,14vw,11rem)] leading-[0.85] tracking-tight">
              {posterDay(event.eventDate) || '·'}
            </span>
            <span className="block mt-4 font-mono uppercase th-text-secondary text-sm tracking-[0.5em] pl-[0.5em]">
              {posterMonth(event.eventDate)}
            </span>
            <span className="block mt-3 font-mono uppercase th-text-muted text-[11px] tracking-[0.25em]">
              {[posterYear(event.eventDate), posterWeekday(event.eventDate)].filter(Boolean).join(' · ')}
            </span>
          </time>
        </div>

        {/* Body block */}
        <div className="flex flex-col gap-5 px-4 sm:px-6 lg:px-10 py-10 lg:py-12">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <span className={`th-badge text-xs ${statusBadgeClass}`}>{statusLabel}</span>
            {isOwner && (
              <div className="flex gap-2">
                <button onClick={() => navigate(`/dashboard?editEvent=${event.id}`)} className="th-btn">
                  Modifier
                </button>
                {isDraft && (
                  <button onClick={publishEvent} className="th-btn-accent">
                    Publier
                  </button>
                )}
              </div>
            )}
          </div>

          <h1 className="font-serif th-text text-[clamp(3rem,7vw,6rem)] leading-[0.95] tracking-tight">
            {event.title}
          </h1>

          {telegramLine && (
            <p className="th-mono-meta th-text tracking-[0.2em]">{telegramLine}</p>
          )}

          {startTime && (
            <p className="th-mono-meta">
              {endTime ? `${startTime} → ${endTime}` : startTime}
            </p>
          )}
        </div>
      </header>

      {/* ───────── Two-column body ───────── */}
      <div className="grid lg:grid-cols-[1fr_360px]">
        {/* Main column */}
        <main className="lg:border-r th-border px-4 sm:px-6 lg:px-10 py-10 lg:py-12">
          {/* À propos */}
          {event.description && (
            <section className="mb-12">
              <p className="th-kicker mb-4">À propos</p>
              <p className="th-serif-body max-w-[62ch] whitespace-pre-line">{event.description}</p>
            </section>
          )}

          {event.description && <hr className="border-t th-border my-12" />}

          {/* Line-up */}
          <section>
            <div className="th-section-head mb-8">
              <div>
                <p className="th-kicker th-kicker-accent mb-3">Line-up</p>
                <h2 className="th-display-md">
                  {isOwner
                    ? `${acceptedBookings.length} artiste${acceptedBookings.length > 1 ? 's' : ''} confirmé${
                        acceptedBookings.length > 1 ? 's' : ''
                      }`
                    : 'Programmation'}
                </h2>
              </div>
              <span className="th-mono-meta">Bookings acceptés</span>
            </div>

            {isOwner ? (
              loadingBookings ? (
                <p className="th-mono-meta">Chargement...</p>
              ) : acceptedBookings.length === 0 ? (
                <p className="th-mono-meta">Line-up en cours d'annonce.</p>
              ) : (
                <div>
                  {acceptedBookings.map((booking, i) => (
                    <div key={booking.id} className="th-event-row">
                      <span className="th-event-row-num">{String(i + 1).padStart(2, '0')}.</span>
                      <span className="th-event-row-title th-display-md flex-1">
                        {booking.artistName || 'Artiste'}
                      </span>
                      <span className="th-badge border-accent text-accent text-xs">Confirmé</span>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <p className="th-mono-meta">Line-up en cours d'annonce.</p>
            )}
          </section>
        </main>

        {/* Aside */}
        <aside className="lg:sticky lg:top-0 self-start px-4 sm:px-6 lg:px-8 py-10 lg:py-12 space-y-6">
          {/* Infos pratiques */}
          <div className="border th-border th-bg-elevated p-6">
            <p className="th-kicker mb-5">Infos pratiques</p>
            <dl className="text-sm">
              {event.eventDate && (
                <div className="flex justify-between gap-4 border-b th-border py-2">
                  <dt className="th-mono-meta th-text-muted">Date</dt>
                  <dd className="th-text font-mono text-[11px] uppercase tracking-[0.1em] text-right">
                    {formatDate(event.eventDate)}
                  </dd>
                </div>
              )}
              {startTime && endTime && (
                <div className="flex justify-between gap-4 border-b th-border py-2">
                  <dt className="th-mono-meta th-text-muted">Horaires</dt>
                  <dd className="th-text font-mono text-[11px] uppercase tracking-[0.1em] text-right">
                    {startTime} → {endTime}
                  </dd>
                </div>
              )}
              {event.venueName && (
                <div className="flex justify-between gap-4 border-b th-border py-2">
                  <dt className="th-mono-meta th-text-muted">Lieu</dt>
                  <dd className="th-text font-mono text-[11px] uppercase tracking-[0.1em] text-right">
                    {event.venueName}
                  </dd>
                </div>
              )}
              {cityCountry && (
                <div className="flex justify-between gap-4 border-b th-border py-2">
                  <dt className="th-mono-meta th-text-muted">Ville</dt>
                  <dd className="th-text font-mono text-[11px] uppercase tracking-[0.1em] text-right">
                    {cityCountry}
                  </dd>
                </div>
              )}
              <div className="flex justify-between gap-4 py-2">
                <dt className="th-mono-meta th-text-muted">Statut</dt>
                <dd
                  className={`font-mono text-[11px] uppercase tracking-[0.1em] text-right ${
                    event.status === 'published' ? 'text-accent' : 'th-text'
                  }`}
                >
                  {statusLabel}
                </dd>
              </div>
            </dl>
          </div>

          {/* Organisateur (owner only) */}
          {isOwner && (
            <div className="border th-border th-bg-elevated p-6 space-y-3">
              <p className="th-kicker mb-4">Vous êtes l'organisateur ?</p>
              <button
                onClick={() => navigate(`/dashboard?editEvent=${event.id}`)}
                className="th-btn w-full"
              >
                Gérer l'événement
              </button>
              <button
                onClick={() => navigate(`/dashboard?editEvent=${event.id}`)}
                className="th-btn w-full"
              >
                Inviter un artiste
              </button>
              {isDraft && (
                <button onClick={publishEvent} className="th-btn-accent w-full">
                  Publier
                </button>
              )}
            </div>
          )}

          {/* Sur la plateforme */}
          <div className="border th-border th-bg-elevated p-6">
            <p className="th-kicker mb-4">Sur la plateforme</p>
            <p className="th-mono-meta mb-5 leading-relaxed">
              Découvrez les artistes disponibles et construisez votre prochaine affiche.
            </p>
            <Link to="/artists" className="th-link-mono">
              Voir l'annuaire →
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
